/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";

import {
	Button,
	Checkbox,
	Select,
	MenuItem,
	Pagination,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
	CircularProgress,
	Tooltip,
	Menu as MuiMenu,
} from "@mui/material";
import { DeleteOutline, ExpandMoreRounded, Add, RemoveRounded, CloseRounded } from "@mui/icons-material";
import { THEME_MAP } from "../theme";
import { auth } from "../services/firebase";
import { useBars, useBarUtils } from "../actions/bars";
import { useUserUtils } from "../actions/users";
import { useStateRegions, useStateRegionActions } from "../actions/stateRegions";
import { buildStateRegionIndex, getStateRegionLabel } from "../utils/stateRegions";
import { BarsFilters } from "../components/BarsFilters";
import { BarStatusFilters, STATUS_COLOR_MAP, STATUS_BACKGROUND_MAP } from "../components/BarStatusFilters";
import { normalizeWhitespace, looksLikeCityName } from "../lib/address";
import { UPDATED_SORTING_COLLECTION } from "../actions/bars";
import { firestore } from "../services/firebase";
import { usePagination } from "../hooks/usePagination";
import { captureError } from "../lib/errorReporting";

const normalizeBarSnapshot = (input) => {
	if (!input) {
		return null;
	}

	if (typeof input?.data === "function") {
		const data = input.data();
		return {
			...data,
			barId: input.id,
			id: input.id
		};
	}

	const barId = input.barId || input.id;
	return {
		...input,
		barId,
		id: barId
	};
};

const parseFiltersFromSearch = (search) => {
	const params = new URLSearchParams(search);
	return {
		searchTerm: params.get("q") || "",
		selectedStateId: params.get("state") || "",
		selectedRegionId: params.get("region") || "",
		selectedSubregionId: params.get("subregion") || "",
		radiusMiles: params.get("radius") || "",
		cityKey: params.get("city") || ""
	};
};

const buildSearchFromFilters = ({
	searchTerm,
	selectedStateId,
	selectedRegionId,
	selectedSubregionId,
	radiusMiles,
	cityKey
}) => {
	const params = new URLSearchParams();
	if (searchTerm) params.set("q", searchTerm);
	if (selectedStateId) params.set("state", selectedStateId);
	if (selectedRegionId) params.set("region", selectedRegionId);
	if (selectedSubregionId) params.set("subregion", selectedSubregionId);
	if (radiusMiles) params.set("radius", radiusMiles);
	if (cityKey) params.set("city", cityKey);
	const query = params.toString();
	return query ? `?${query}` : "";
};

const resolveStateId = (value, states = []) => {
	if (!value) return "";
	const match = (states || []).find(
		(state) => state.id === value || state.abbr === value || state.name === value
	);
	return match?.id || value;
};

const resolveRegionId = (value, stateId, states = []) => {
	if (!value) return "";
	const state = (states || []).find((item) => item.id === stateId);
	const candidates = state?.regions || states.flatMap((item) => item.regions || []);
	const match = (candidates || []).find((region) => region.id === value || region.name === value);
	return match?.id || value;
};

const resolveSubregionId = (value, stateId, regionId, states = []) => {
	if (!value) return "";
	const state = (states || []).find((item) => item.id === stateId);
	const region = state?.regions?.find((item) => item.id === regionId)
		|| states
			.flatMap((item) => item.regions || [])
			.find((item) => item.id === regionId);
	const candidates = region?.subregions || states.flatMap((item) =>
		(item.regions || []).flatMap((regionItem) => regionItem.subregions || [])
	);
	const match = (candidates || []).find(
		(subregion) => subregion.id === value || subregion.name === value
	);
	return match?.id || value;
};

const collator = new Intl.Collator("en", { sensitivity: "base" });

const addedDateFormatter = new Intl.DateTimeFormat("en-US", {
	year: "numeric",
	month: "short",
	day: "numeric"
});

const parseAddedDate = (value) => {
	if (!value) {
		return null;
	}
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatAddedDate = (value) => {
	const parsed = parseAddedDate(value);
	return parsed ? addedDateFormatter.format(parsed) : "";
};

const DEFAULT_SORT_MODEL = [{ field: "name", direction: "asc" }];
const EARTH_RADIUS_MILES = 3958.8;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const BAR_GRID_TEMPLATE = "2fr 1.1fr 1fr 2fr 1.2fr 0.8fr 0.9fr 40px";
const BAR_GRID_TEMPLATE_ASSIGN = "40px 2fr 1.1fr 1fr 2fr 1.2fr 0.8fr 0.9fr 40px";
const BARS_FILTERS_STORAGE_KEY = "admin-bars-filters";
const USA_STATE_MARKERS = new Set(["USA", "US", "U.S.A", "U.S."]);
const STATUS_STYLE_BY_KEY = {
	completed: {
		background: STATUS_BACKGROUND_MAP.completed,
		textColor: THEME_MAP["color-basic-900"] || "#1f1f1f",
		hoverBackground: STATUS_COLOR_MAP.completed
	},
	updated: {
		background: STATUS_BACKGROUND_MAP.updated,
		textColor: THEME_MAP["color-basic-900"] || "#1f1f1f",
		hoverBackground: STATUS_COLOR_MAP.updated
	},
	incomplete: {
		background: STATUS_BACKGROUND_MAP.incomplete,
		textColor: THEME_MAP["color-basic-900"] || "#1f1f1f",
		hoverBackground: STATUS_COLOR_MAP.incomplete
	}
};

const parseDeleteEmailAllowList = () => {
	const raw = process.env.REACT_APP_CAN_DELETE_EMAILS;
	if (!raw) {
		return new Set();
	}

	try {
		const parsed = JSON.parse(raw);
		if (Array.isArray(parsed)) {
			return new Set(
				parsed
					.map((email) => String(email || "").trim().toLowerCase())
					.filter(Boolean)
			);
		}
	} catch (_error) {
		// fall through to string parsing
	}

	return new Set(
		raw
			.split(",")
			.map((value) => value.replace(/[[\]\s"']/g, ""))
			.map((value) => value.trim().toLowerCase())
			.filter(Boolean)
	);
};

const normalizeStateValue = (value = "") => {
	const normalized = normalizeWhitespace(String(value || ""));
	if (!normalized) {
		return "";
	}
	const upper = normalized.toUpperCase();
	if (USA_STATE_MARKERS.has(upper)) {
		return "";
	}
	const match = upper.match(/\b([A-Z]{2})\b/);
	return match ? match[1] : normalized;
};

const CAN_DELETE_EMAILS = parseDeleteEmailAllowList();

const toRadians = (value) => (value * Math.PI) / 180;

const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
	if (
		typeof lat1 !== "number" ||
		typeof lon1 !== "number" ||
		typeof lat2 !== "number" ||
		typeof lon2 !== "number"
	) {
		return null;
	}

	const deltaLat = toRadians(lat2 - lat1);
	const deltaLon = toRadians(lon2 - lon1);
	const a =
		Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
		Math.cos(toRadians(lat1)) *
			Math.cos(toRadians(lat2)) *
			Math.sin(deltaLon / 2) *
			Math.sin(deltaLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return EARTH_RADIUS_MILES * c;
};

const getBarCityLabel = (bar) => {
	if (!bar) {
		return "";
	}

	const candidates = [
		normalizeWhitespace(bar.city || "")
	].filter(Boolean);

	const match = candidates.find((value) => looksLikeCityName(value));
	return match || "";
};

export function Bars() {
	const history = useHistory();
	const { search } = useLocation();
	const { bars, endOfResults, loading, totalCount } = useBars();
	const { getNext, clearSelectedBar, setBarsQuery, assignRegionsToBars, setSortOrder } = useBarUtils();
	const stateRegions = useStateRegions();
	const { fetchStateRegions } = useStateRegionActions();
	const currentUserEmail = (auth?.currentUser?.email || "").toLowerCase();
	const canDeleteBars = useMemo(() => CAN_DELETE_EMAILS.has(currentUserEmail), [currentUserEmail]);
	const initialFilters = useMemo(() => parseFiltersFromSearch(search), [search]);

	const [searchTerm, setSearchTerm] = useState(() => initialFilters.searchTerm);
	const [draftSearchTerm, setDraftSearchTerm] = useState(() => initialFilters.searchTerm);
	const [selectedCity, setSelectedCity] = useState(null);
	const [draftSelectedCity, setDraftSelectedCity] = useState(null);
	const [selectedStateId, setSelectedStateId] = useState(() => initialFilters.selectedStateId);
	const [draftSelectedStateId, setDraftSelectedStateId] = useState(() => initialFilters.selectedStateId);
	const [selectedRegionId, setSelectedRegionId] = useState(() => initialFilters.selectedRegionId);
	const [draftSelectedRegionId, setDraftSelectedRegionId] = useState(() => initialFilters.selectedRegionId);
	const [selectedSubregionId, setSelectedSubregionId] = useState(() => initialFilters.selectedSubregionId);
	const [draftSelectedSubregionId, setDraftSelectedSubregionId] = useState(() => initialFilters.selectedSubregionId);
	const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);
	const [radiusMiles, setRadiusMiles] = useState(() => initialFilters.radiusMiles);
	const [draftRadiusMiles, setDraftRadiusMiles] = useState(() => initialFilters.radiusMiles);
	const [assignMode, setAssignMode] = useState(false);
	const [selectedBarIds, setSelectedBarIds] = useState(() => new Set());
	const [confirmAssignOpen, setConfirmAssignOpen] = useState(false);
	const [assigning, setAssigning] = useState(false);
	const [assignError, setAssignError] = useState(null);
	const [updateStatusMap, setUpdateStatusMap] = useState(() => new Map());
	const [statusFilters, setStatusFilters] = useState(() => new Set());
	const [legacySubregionNameMap, setLegacySubregionNameMap] = useState(() => new Map());
	const [cityQueryKey, setCityQueryKey] = useState(() => initialFilters.cityKey);
	const [draftCityQueryKey, setDraftCityQueryKey] = useState(() => initialFilters.cityKey);
	const didRestoreFiltersRef = useRef(false);
	const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);

	const stateRegionIndex = useMemo(() => buildStateRegionIndex(stateRegions), [stateRegions]);
	const assignRegionName = useMemo(() => {
		const { stateName, regionName, subregionName } = getStateRegionLabel(
			{ stateId: selectedStateId, regionId: selectedRegionId, subregionId: selectedSubregionId },
			stateRegionIndex
		);
		return [stateName, regionName, subregionName].filter(Boolean).join(" / ");
	}, [selectedRegionId, selectedStateId, selectedSubregionId, stateRegionIndex]);

	const sortKey = useMemo(
		() => sortModel.map((entry) => `${entry.field}:${entry.direction}`).join(","),
		[sortModel]
	);
	// Only a lone name sort can be handled server-side (paginated); any other or
	// multi-column sort needs all rows loaded for a correct client-side sort.
	const isServerNameSort = sortModel.length === 1 && sortModel[0].field === "name";
	const hasCustomSort = sortKey !== "name:asc";

	useEffect(() => {
		fetchStateRegions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (didRestoreFiltersRef.current) {
			return;
		}
		if (!search) {
			const stored = window.localStorage.getItem(BARS_FILTERS_STORAGE_KEY);
			if (stored) {
				history.replace({ search: stored });
			}
		}
		didRestoreFiltersRef.current = true;
	}, [history, search]);

	useEffect(() => {
		if (search) {
			window.localStorage.setItem(BARS_FILTERS_STORAGE_KEY, search);
			return;
		}
		window.localStorage.removeItem(BARS_FILTERS_STORAGE_KEY);
	}, [search]);

	useEffect(() => {
		firestore
			.collection("Regions")
			.get()
			.then((snapshot) => {
				const map = new Map();
				snapshot.forEach((doc) => {
					const data = doc.data() || {};
					if (data.Name) {
						map.set(doc.id, data.Name);
					}
				});
				setLegacySubregionNameMap(map);
			})
			.catch((error) => {
				console.error("Failed to fetch legacy regions", error);
				captureError(error, { action: "fetchLegacyRegions" });
			});
	}, []);

	const getBarUpdateStatus = useCallback(
		(barId) => {
			if (!barId) {
				return "incomplete";
			}
			const record = updateStatusMap.get(barId);
			if (!record) {
				return "incomplete";
			}
			if (record.markedAsCompleted) {
				return "completed";
			}
			return "updated";
		},
		[updateStatusMap]
	);

	useEffect(() => {
		const unsubscribe = firestore
			.collection(UPDATED_SORTING_COLLECTION)
			.onSnapshot((snapshot) => {
				const nextMap = new Map();
				snapshot.forEach((doc) => {
					const data = doc.data() || {};
					nextMap.set(doc.id, {
						...data,
						updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
						completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : data.completedAt
					});
				});
				setUpdateStatusMap(nextMap);
			}, (err) => {
				captureError(err, { view: "Bars", action: "updateStatusMap.onSnapshot" });
				console.error("Bars updateStatusMap snapshot error", err);
			});

		return () => {
			unsubscribe?.();
		};
	}, []);

	const selectedCityCenter = useMemo(() => {
		if (!selectedCity?.key) {
			return null;
		}

		const matches = (bars || []).filter((bar) => {
			const label = getBarCityLabel(bar);
			return label && label.toLowerCase() === selectedCity.key;
		});
		const withCoordinates = matches.filter(
			(bar) => Number.isFinite(bar.latitude) && Number.isFinite(bar.longitude)
		);
		if (!withCoordinates.length) {
			return null;
		}

		const { latitude, longitude } = withCoordinates.reduce(
			(acc, bar) => ({
				latitude: acc.latitude + bar.latitude,
				longitude: acc.longitude + bar.longitude
			}),
			{ latitude: 0, longitude: 0 }
		);

		return {
			latitude: latitude / withCoordinates.length,
			longitude: longitude / withCoordinates.length
		};
	}, [bars, selectedCity]);

	const radiusValue = Number.parseFloat(radiusMiles);
	const canComputeDistances = Boolean(selectedCityCenter);

	useEffect(() => {
		const params = new URLSearchParams(search);
		const q = params.get("q") || "";
		if (q !== searchTerm) {
			setSearchTerm(q);
		}
		if (q !== draftSearchTerm) {
			setDraftSearchTerm(q);
		}
		setBarsQuery(q);
	}, [search, setBarsQuery]);

	useEffect(() => {
		const parsed = parseFiltersFromSearch(search);
		const resolvedStateId = resolveStateId(parsed.selectedStateId, stateRegions);
		const resolvedRegionId = resolveRegionId(parsed.selectedRegionId, resolvedStateId, stateRegions);
		const resolvedSubregionId = resolveSubregionId(
			parsed.selectedSubregionId,
			resolvedStateId,
			resolvedRegionId,
			stateRegions
		);

		if (resolvedStateId !== selectedStateId) {
			setSelectedStateId(resolvedStateId);
		}
		if (resolvedRegionId !== selectedRegionId) {
			setSelectedRegionId(resolvedRegionId);
		}
		if (resolvedSubregionId !== selectedSubregionId) {
			setSelectedSubregionId(resolvedSubregionId);
		}
		if (parsed.radiusMiles !== radiusMiles) {
			setRadiusMiles(parsed.radiusMiles);
		}
		if (parsed.cityKey !== cityQueryKey) {
			setCityQueryKey(parsed.cityKey);
		}
		if (resolvedStateId !== draftSelectedStateId) {
			setDraftSelectedStateId(resolvedStateId);
		}
		if (resolvedRegionId !== draftSelectedRegionId) {
			setDraftSelectedRegionId(resolvedRegionId);
		}
		if (resolvedSubregionId !== draftSelectedSubregionId) {
			setDraftSelectedSubregionId(resolvedSubregionId);
		}
		if (parsed.radiusMiles !== draftRadiusMiles) {
			setDraftRadiusMiles(parsed.radiusMiles);
		}
		if (parsed.cityKey !== draftCityQueryKey) {
			setDraftCityQueryKey(parsed.cityKey);
		}
	}, [search, stateRegions]);

	useEffect(() => {
		if (!cityQueryKey) {
			if (selectedCity) {
				setSelectedCity(null);
			}
			return;
		}
		if (selectedCity?.key === cityQueryKey) {
			return;
		}
		if (!bars?.length) {
			return;
		}
		const match = (bars || []).reduce((found, bar) => {
			if (found) return found;
			const label = getBarCityLabel(bar);
			if (label && label.toLowerCase() === cityQueryKey) {
				return { key: cityQueryKey, label };
			}
			return null;
		}, null);
		if (match) {
			setSelectedCity(match);
		}
	}, [bars, cityQueryKey, selectedCity]);

	useEffect(() => {
		if (!draftCityQueryKey) {
			if (draftSelectedCity) {
				setDraftSelectedCity(null);
			}
			return;
		}
		if (draftSelectedCity?.key === draftCityQueryKey) {
			return;
		}
		if (!bars?.length) {
			return;
		}
		const match = (bars || []).reduce((found, bar) => {
			if (found) return found;
			const label = getBarCityLabel(bar);
			if (label && label.toLowerCase() === draftCityQueryKey) {
				return { key: draftCityQueryKey, label };
			}
			return null;
		}, null);
		if (match) {
			setDraftSelectedCity(match);
		}
	}, [bars, draftCityQueryKey, draftSelectedCity]);




	useEffect(() => {
		if (!assignMode) {
			setSelectedBarIds(new Set());
		}
	}, [assignMode]);

	const cityOptions = useMemo(() => {
		const map = new Map();
		(bars || []).forEach((bar) => {
			const cityLabel = getBarCityLabel(bar);
			if (!cityLabel) {
				return;
			}
			const key = cityLabel.toLowerCase();
			if (!map.has(key)) {
				map.set(key, {
					key,
					label: cityLabel
				});
			}
		});
		return Array.from(map.values()).sort((a, b) => collator.compare(a.label, b.label));
	}, [bars]);

	const shouldAutoLoadAll = useMemo(
		() =>
			!searchTerm &&
			(!isServerNameSort ||
				assignMode ||
				Boolean(selectedStateId) ||
				Boolean(selectedRegionId) ||
				Boolean(selectedSubregionId) ||
				Boolean(selectedCity?.key) ||
				Boolean(radiusMiles)),
		[searchTerm, isServerNameSort, assignMode, selectedCity?.key, selectedRegionId, selectedStateId, selectedSubregionId, radiusMiles]
	);

	useEffect(() => {
		if (!shouldAutoLoadAll || endOfResults || loading) {
			return;
		}

		getNext();
	}, [endOfResults, getNext, loading, shouldAutoLoadAll]);

	const processedBars = useMemo(() => {
		const unique = new Map();
		(bars || []).forEach((bar) => {
			const key = bar?.barId || bar?.id;
			if (!key || unique.has(key)) {
				return;
			}
			unique.set(key, bar);
		});
		const list = Array.from(unique.values());

		const withDistance = list.map((bar) => {
			const distance = selectedCityCenter &&
				Number.isFinite(bar.latitude) &&
				Number.isFinite(bar.longitude)
					? calculateDistanceMiles(
						selectedCityCenter.latitude,
						selectedCityCenter.longitude,
						bar.latitude,
						bar.longitude
					)
					: null;
			const status = getBarUpdateStatus(bar.barId);
			return { bar, distance, status };
		});

		const radiusFilterActive =
			Boolean(selectedCityCenter) && Number.isFinite(radiusValue) && radiusValue > 0;

		let filtered = withDistance;
		if (radiusFilterActive) {
			filtered = filtered.filter((item) => {
				if (item.distance !== null && item.distance <= radiusValue) {
					return true;
				}
				if (selectedCity?.key) {
					const label = getBarCityLabel(item.bar);
					return label && label.toLowerCase() === selectedCity.key;
				}
				return false;
			});
		} else if (selectedCity?.key) {
			filtered = filtered.filter((item) => {
				const label = getBarCityLabel(item.bar);
				return label && label.toLowerCase() === selectedCity.key;
			});
		}

		const resolveRegionPath = (bar) => {
			const stateValue = bar?.stateId || null;
			const regionValue = bar?.regionId || null;
			const subregionValue = bar?.subregionId || null;
			if (!regionValue && subregionValue && stateRegionIndex?.subregionById) {
				const resolved = stateRegionIndex.subregionById.get(subregionValue);
				return [stateValue || resolved?.stateId || null, resolved?.regionId || null, subregionValue];
			}
			return [stateValue, regionValue, subregionValue];
		};

		const selectedState = selectedStateId
			? stateRegions.find((state) => state.id === selectedStateId)
			: null;
		const selectedStateValues = selectedState
			? new Set([selectedStateId, selectedState.abbr, selectedState.name].filter(Boolean))
			: new Set(selectedStateId ? [selectedStateId] : []);

		if (selectedStateId) {
			filtered = filtered.filter((item) => {
				const [stateValue] = resolveRegionPath(item.bar);
				return stateValue && selectedStateValues.has(stateValue);
			});
		}

		if (!assignMode && selectedRegionId) {
			filtered = filtered.filter((item) => {
				const [, regionValue] = resolveRegionPath(item.bar);
				return regionValue === selectedRegionId;
			});
		}

		if (!assignMode && selectedSubregionId) {
			filtered = filtered.filter((item) => {
				const [, , subregionValue] = resolveRegionPath(item.bar);
				return subregionValue === selectedSubregionId;
			});
		}

		if (assignMode) {
			filtered = filtered.filter((item) => {
				const [, regionValue, subregionValue] = resolveRegionPath(item.bar);
				const missingRegion = !regionValue || regionValue === "";
				const missingSubregion = !subregionValue || subregionValue === "";
				return missingRegion || missingSubregion;
			});
		}

		if (statusFilters.size > 0) {
			filtered = filtered.filter((item) =>
				statusFilters.has(item.status || getBarUpdateStatus(item.bar.barId))
			);
		}

		const getRegionName = (bar) =>
			normalizeWhitespace(
				getStateRegionLabel(
					{ stateId: bar.stateId, regionId: bar.regionId, subregionId: bar.subregionId },
					stateRegionIndex
				).regionName || ""
			);

		const getSubRegionName = (bar) =>
			normalizeWhitespace(
				getStateRegionLabel(
					{ stateId: bar.stateId, regionId: bar.regionId, subregionId: bar.subregionId },
					stateRegionIndex
				).subregionName || legacySubregionNameMap.get(bar.subregionId) || bar.subregionId || ""
			);

		const getValueForField = (bar, field) => {
			switch (field) {
				case "region":
					return getRegionName(bar);
				case "subregion":
					return getSubRegionName(bar);
				case "street":
					return normalizeWhitespace(bar.address_1 || "");
				case "city":
					return normalizeWhitespace(bar.city || "");
				case "state":
					return normalizeWhitespace(bar.state || "");
				case "added": {
					const parsed = parseAddedDate(bar.createdAt);
					// Zero-padded epoch millis so string collation matches chronological order.
					return parsed ? String(parsed.getTime()).padStart(16, "0") : "";
				}
				case "name":
					return normalizeWhitespace(bar.barName || "");
			default:
				return normalizeWhitespace(bar.barName || "");
			}
		};

		const activeSort = sortModel.length ? sortModel : DEFAULT_SORT_MODEL;
		const sorted = [...filtered].sort((left, right) => {
			for (const { field, direction } of activeSort) {
				const cmp = collator.compare(
					getValueForField(left.bar, field),
					getValueForField(right.bar, field)
				);
				if (cmp !== 0) {
					return direction === "asc" ? cmp : -cmp;
				}
			}
			// Stable tie-break by name.
			return collator.compare(left.bar.barName || "", right.bar.barName || "");
		});

		return sorted;
	}, [assignMode, bars, getBarUpdateStatus, legacySubregionNameMap, radiusValue, selectedCity, selectedCityCenter, selectedRegionId, selectedStateId, selectedSubregionId, sortModel, stateRegionIndex, stateRegions, statusFilters]);

	const canPaginate = !searchTerm;
	const hasActiveFilters = Boolean(
		selectedCity?.key ||
		selectedStateId ||
		selectedRegionId ||
		selectedSubregionId ||
		radiusMiles ||
		statusFilters.size > 0
	);

	const {
		pageSize,
		paginationPage,
		paginationCount,
		paginatedItems,
		pageSummaryText,
		handlePageChange,
		handlePageSizeChange,
		resetPagination
	} = usePagination({
		items: processedBars,
		defaultPageSize: DEFAULT_PAGE_SIZE,
		loading,
		canPaginate,
		endOfResults,
		totalCount: canPaginate && !hasActiveFilters ? totalCount : null,
		getNext,
		itemLabelSingular: "bar",
		itemLabelPlural: "bars"
	});

	const visibleBarIds = useMemo(
		() => paginatedItems.map(({ bar }) => bar?.barId).filter(Boolean),
		[paginatedItems]
	);

	const handleSearchChange = useCallback(
		(event) => {
			const value = event.target.value;
			setDraftSearchTerm(value);
		},
		[]
	);

	const headerColumns = useMemo(
		() => [
			...(assignMode ? [{ key: "select", label: "Select", sortable: false }] : []),
			{ key: "name", label: "Name", sortable: true },
			{ key: "region", label: "Region", sortable: true },
			{ key: "subregion", label: "SubRegion", sortable: true },
			{ key: "street", label: "Street", sortable: true },
			{ key: "city", label: "City", sortable: true },
			{ key: "state", label: "State", sortable: true },
			{ key: "added", label: "Added", sortable: true },
			{ key: "actions", label: "", sortable: false }
		],
		[assignMode]
	);

	useEffect(() => {
		if (!assignMode) {
			return;
		}
		setSelectedBarIds((prev) => {
			const next = new Set();
			visibleBarIds.forEach((id) => {
				if (prev.has(id)) {
					next.add(id);
				}
			});
			return next;
		});
	}, [assignMode, visibleBarIds]);

	const handleCityChange = useCallback(
		(event, option) => {
			setDraftSelectedCity(option);
			setDraftCityQueryKey(option?.key || "");
			setDraftRadiusMiles("");
			if (!option) {
				setSortModel(DEFAULT_SORT_MODEL);
				setSortOrder("bar_name_lower", true);
			}
		},
		[setSortOrder]
	);

	const handleStateChange = useCallback((event) => {
		const nextStateId = event.target.value || "";
		setDraftSelectedStateId(nextStateId);
		setDraftSelectedRegionId("");
		setDraftSelectedSubregionId("");
	}, []);

	const handleRegionChange = useCallback((event) => {
		const nextRegionId = event.target.value || "";
		setDraftSelectedRegionId(nextRegionId);
		setDraftSelectedSubregionId("");
	}, []);

	const handleSubregionChange = useCallback((event) => {
		setDraftSelectedSubregionId(event.target.value || "");
	}, []);

	const handleAssignModeToggle = useCallback(() => {
		setAssignMode((current) => {
			const next = !current;
			if (next) {
				setSelectedStateId("");
				setSelectedRegionId("");
				setSelectedSubregionId("");
			}
			setAssignError(null);
			return next;
		});
	}, []);



	const handleStatusFilterChange = useCallback((nextSet) => {
		setStatusFilters(new Set(nextSet));
		resetPagination();
	}, [resetPagination]);

	// Multi-column sort. New columns are added above Name (which stays pinned as
	// the last tie-break, since names are unique — anything below it is dead).
	// Clicking a sorted column flips asc -> desc, then removes it.
	const handleColumnSort = useCallback((field) => {
		setSortModel((current) => {
			const model = current.map((entry) => ({ ...entry }));
			const idx = model.findIndex((entry) => entry.field === field);

			if (idx === -1) {
				const entry = { field, direction: "asc" };
				const nameIdx = field === "name" ? -1 : model.findIndex((e) => e.field === "name");
				if (nameIdx === -1) {
					model.push(entry);
				} else {
					model.splice(nameIdx, 0, entry);
				}
			} else if (model[idx].direction === "asc") {
				model[idx].direction = "desc";
			} else {
				model.splice(idx, 1);
			}

			const next = model.length ? model : DEFAULT_SORT_MODEL;
			if (next.length === 1 && next[0].field === "name") {
				setSortOrder("bar_name_lower", next[0].direction === "asc");
			} else {
				setSortOrder("bar_name_lower", true);
			}
			return next;
		});
	}, [setSortOrder]);

	const handleCancelSort = useCallback(() => {
		setSortModel(DEFAULT_SORT_MODEL);
		setSortOrder("bar_name_lower", true);
	}, [setSortOrder]);

	const handleToggleSelectBar = useCallback((id) => {
		setSelectedBarIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		setSelectedBarIds((prev) => {
			const allIds = visibleBarIds;
			if (prev.size === allIds.length && allIds.length > 0) {
				return new Set();
			}
			return new Set(allIds);
		});
	}, [visibleBarIds]);

	const handleClearFilters = useCallback(() => {
		setDraftSelectedCity(null);
		setDraftCityQueryKey("");
		setDraftSelectedStateId("");
		setDraftSelectedRegionId("");
		setDraftSelectedSubregionId("");
		setDraftRadiusMiles("");
		setDraftSearchTerm("");

		setSelectedCity(null);
		setCityQueryKey("");
		setSelectedStateId("");
		setSelectedRegionId("");
		setSelectedSubregionId("");
		setRadiusMiles("");
		setSearchTerm("");
		setBarsQuery("");
		history.replace({ search: "" });
		window.localStorage.removeItem(BARS_FILTERS_STORAGE_KEY);

		setSortModel(DEFAULT_SORT_MODEL);
		setSortOrder("bar_name_lower", true);
		resetPagination();
	}, [history, resetPagination, setBarsQuery, setSortOrder]);

	const handleRemoveFilter = useCallback((filterType) => {
		let nextSearchTerm = searchTerm || "";
		let nextStateId = selectedStateId || "";
		let nextRegionId = selectedRegionId || "";
		let nextSubregionId = selectedSubregionId || "";
		let nextCity = selectedCity || null;
		let nextCityKey = cityQueryKey || "";
		let nextRadius = radiusMiles || "";

		switch (filterType) {
			case "search":
				nextSearchTerm = "";
				break;
			case "state":
				nextStateId = "";
				nextRegionId = "";
				nextSubregionId = "";
				break;
			case "region":
				nextRegionId = "";
				nextSubregionId = "";
				break;
			case "subregion":
				nextSubregionId = "";
				break;
			case "city":
				nextCity = null;
				nextCityKey = "";
				nextRadius = "";
				break;
			case "radius":
				nextRadius = "";
				break;
			default:
				break;
		}

		setDraftSearchTerm(nextSearchTerm);
		setDraftSelectedStateId(nextStateId);
		setDraftSelectedRegionId(nextRegionId);
		setDraftSelectedSubregionId(nextSubregionId);
		setDraftSelectedCity(nextCity);
		setDraftCityQueryKey(nextCityKey);
		setDraftRadiusMiles(nextRadius);

		setSearchTerm(nextSearchTerm);
		setSelectedStateId(nextStateId);
		setSelectedRegionId(nextRegionId);
		setSelectedSubregionId(nextSubregionId);
		setSelectedCity(nextCity);
		setCityQueryKey(nextCityKey);
		setRadiusMiles(nextRadius);
		setBarsQuery(nextSearchTerm || "");

		const nextSearch = buildSearchFromFilters({
			searchTerm: nextSearchTerm,
			selectedStateId: nextStateId,
			selectedRegionId: nextRegionId,
			selectedSubregionId: nextSubregionId,
			radiusMiles: nextRadius,
			cityKey: nextCityKey
		});
		history.replace({ search: nextSearch });
		if (nextSearch) {
			window.localStorage.setItem(BARS_FILTERS_STORAGE_KEY, nextSearch);
		} else {
			window.localStorage.removeItem(BARS_FILTERS_STORAGE_KEY);
		}
		resetPagination();
	}, [cityQueryKey, history, radiusMiles, resetPagination, searchTerm, selectedCity, selectedRegionId, selectedStateId, selectedSubregionId, setBarsQuery]);

	const handleApplyFilters = useCallback(() => {
		setSelectedCity(draftSelectedCity);
		setCityQueryKey(draftCityQueryKey || "");
		setSelectedStateId(draftSelectedStateId || "");
		setSelectedRegionId(draftSelectedRegionId || "");
		setSelectedSubregionId(draftSelectedSubregionId || "");
		setRadiusMiles(draftRadiusMiles || "");
		setSearchTerm(draftSearchTerm || "");
		setBarsQuery(draftSearchTerm || "");
		const nextSearch = buildSearchFromFilters({
			searchTerm: draftSearchTerm || "",
			selectedStateId: draftSelectedStateId || "",
			selectedRegionId: draftSelectedRegionId || "",
			selectedSubregionId: draftSelectedSubregionId || "",
			radiusMiles: draftRadiusMiles || "",
			cityKey: draftCityQueryKey || ""
		});
		history.replace({ search: nextSearch });
		if (nextSearch) {
			window.localStorage.setItem(BARS_FILTERS_STORAGE_KEY, nextSearch);
		} else {
			window.localStorage.removeItem(BARS_FILTERS_STORAGE_KEY);
		}
		resetPagination();
	}, [
		draftCityQueryKey,
		draftRadiusMiles,
		draftSearchTerm,
		draftSelectedCity,
		draftSelectedRegionId,
		draftSelectedStateId,
		draftSelectedSubregionId,
		history,
		resetPagination,
		setBarsQuery
	]);

	const hasPendingFilters = useMemo(() => {
		const appliedCityKey = selectedCity?.key || "";
		return (
			(draftSearchTerm || "") !== (searchTerm || "") ||
			(draftSelectedStateId || "") !== (selectedStateId || "") ||
			(draftSelectedRegionId || "") !== (selectedRegionId || "") ||
			(draftSelectedSubregionId || "") !== (selectedSubregionId || "") ||
			(draftRadiusMiles || "") !== (radiusMiles || "") ||
			(draftCityQueryKey || "") !== (appliedCityKey || "")
		);
	}, [
		draftCityQueryKey,
		draftRadiusMiles,
		draftSearchTerm,
		draftSelectedRegionId,
		draftSelectedStateId,
		draftSelectedSubregionId,
		radiusMiles,
		searchTerm,
		selectedCity,
		selectedRegionId,
		selectedStateId,
		selectedSubregionId
	]);

	useEffect(() => {
		resetPagination();
	}, [assignMode, radiusMiles, searchTerm, selectedCity, selectedRegionId, selectedStateId, selectedSubregionId, sortKey, resetPagination]);


	const handleApplyRegionsClick = useCallback(() => {
		if (!selectedStateId || !selectedRegionId) {
			setAssignError("Select a state and region before applying.");
			return;
		}
		if (selectedBarIds.size === 0) {
			setAssignError("Select at least one bar to assign.");
			return;
		}
		setAssignError(null);
		setConfirmAssignOpen(true);
	}, [selectedRegionId, selectedStateId, selectedBarIds]);

	const handleCancelAssign = useCallback(() => {
		setConfirmAssignOpen(false);
		setAssignError(null);
	}, []);

	const handleAssignConfirm = useCallback(async () => {
		setAssigning(true);
		setAssignError(null);

		try {
			const assignState = stateRegions.find((state) => state.id === selectedStateId);
			const stateValue = assignState?.abbr || assignState?.name || selectedStateId;
			await assignRegionsToBars(Array.from(selectedBarIds), {
				stateId: stateValue,
				regionId: selectedRegionId,
				subregionId: selectedSubregionId
			});
			setSelectedBarIds(new Set());
			setConfirmAssignOpen(false);
		} catch (error) {
			setAssignError(error?.message || "Failed to assign regions");
			captureError(error, { action: "assignRegionsToBars" });
		} finally {
			setAssigning(false);
		}
	}, [assignRegionsToBars, selectedBarIds, selectedRegionId, selectedStateId, selectedSubregionId]);


	const navigateToAddBar = useCallback(() => {
		clearSelectedBar();
		history.push("/bar/new");
	}, [clearSelectedBar, history]);

	const addMenuOpen = Boolean(addMenuAnchorEl);
	const handleAddMenuOpen = useCallback((event) => {
		setAddMenuAnchorEl(event.currentTarget);
	}, []);
	const handleAddMenuClose = useCallback(() => {
		setAddMenuAnchorEl(null);
	}, []);

	return (
		<Container>
			<BarsFilters
				searchTerm={draftSearchTerm}
				appliedSearchTerm={searchTerm}
				onSearchChange={handleSearchChange}
				cityOptions={cityOptions}
				selectedCity={draftSelectedCity}
				appliedCity={selectedCity}
				onCityChange={handleCityChange}
				stateRegions={stateRegions}
				selectedStateId={draftSelectedStateId}
				appliedStateId={selectedStateId}
				selectedRegionId={draftSelectedRegionId}
				appliedRegionId={selectedRegionId}
				selectedSubregionId={draftSelectedSubregionId}
				appliedSubregionId={selectedSubregionId}
				onStateChange={handleStateChange}
				onRegionChange={handleRegionChange}
				onSubregionChange={handleSubregionChange}
				sortMode={sortKey}
				canComputeDistances={canComputeDistances}
				radiusMiles={draftRadiusMiles}
				appliedRadiusMiles={radiusMiles}
				onRadiusChange={setDraftRadiusMiles}
				onClearFilters={handleClearFilters}
				onApplyFilters={handleApplyFilters}
				onRemoveFilter={handleRemoveFilter}
				applyDisabled={!hasPendingFilters}
			/>

			<BarStatusFilters
				selectedStatuses={statusFilters}
				onChange={handleStatusFilterChange}
			/>


			{assignMode && (
				<div className="assign-actions">
					<Button
						variant="outlined"
						color="inherit"
						onClick={handleAssignModeToggle}
					>
						Cancel
					</Button>

					<Button
						variant="contained"
						color="success"
						onClick={handleApplyRegionsClick}
						disabled={assigning || selectedBarIds.size === 0 || !selectedStateId || !selectedRegionId}
					>
						{assigning ? "Applying..." : `Apply Regions (${selectedBarIds.size})`}
					</Button>

					{assignError && (
						<span className="assign-error">{assignError}</span>
					)}
				</div>
			)}

			<div className="results-meta">
				<span>{pageSummaryText}</span>
				{loading ? <span className="loading-indicator">Loading...</span> : null}
				<div className="results-meta-spacer" />
				<IconButton
					size="small"
					onClick={handleAddMenuOpen}
					className="add-bar-btn"
				>
					<Add />
				</IconButton>
				<MuiMenu
					anchorEl={addMenuAnchorEl}
					open={addMenuOpen}
					onClose={handleAddMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
				>
					<MenuItem onClick={() => { handleAddMenuClose(); navigateToAddBar(); }}>Create New Bar</MenuItem>
					<MenuItem onClick={() => { handleAddMenuClose(); handleAssignModeToggle(); }}>Assign Regions</MenuItem>
				</MuiMenu>
			</div>

			{assignMode && (
				<div className="selection-header">
					<Checkbox
						checked={selectedBarIds.size === visibleBarIds.length && visibleBarIds.length > 0}
						indeterminate={selectedBarIds.size > 0 && selectedBarIds.size < visibleBarIds.length}
						onChange={handleToggleSelectAll}
					/>
					<span>
						Select bars without a subregion ({selectedBarIds.size} selected of {visibleBarIds.length})
					</span>
				</div>
			)}

			<div className="bars-container">
				<BarHeader $assignMode={assignMode}>
					{headerColumns.map((column) => {
						const sortIndex = column.sortable
							? sortModel.findIndex((entry) => entry.field === column.key)
							: -1;
						const sortEntry = sortIndex >= 0 ? sortModel[sortIndex] : null;
						const showOrder = Boolean(sortEntry) && sortModel.length > 1;
						return (
							<div
								key={column.key}
								className={`header-cell${column.sortable ? " sortable" : ""}${sortEntry ? " active" : ""}${column.key === "actions" ? " actions" : ""}`}
								onClick={column.sortable ? () => handleColumnSort(column.key) : undefined}
								role={column.sortable ? "button" : undefined}
								title={column.sortable ? "Click to sort · click another column to add · click again to flip or remove" : undefined}
							>
								<span className="label">{column.label}</span>
								{column.sortable && (
									sortEntry ? (
										<span className="sort-indicator">
											<SortChevron $active $direction={sortEntry.direction} />
											{showOrder && <span className="sort-order">{sortIndex + 1}</span>}
										</span>
									) : (
										<SortDash />
									)
								)}
								{column.key === "actions" && hasCustomSort && (
									<Tooltip title="Cancel sorting">
										<IconButton
											size="small"
											className="cancel-sort"
											onClick={(e) => { e.stopPropagation(); handleCancelSort(); }}
											aria-label="Cancel sorting"
										>
											<CloseRounded fontSize="inherit" />
										</IconButton>
									</Tooltip>
								)}
							</div>
						);
					})}
				</BarHeader>

				{paginatedItems.map(({ bar, status }) => (
					<BarListItem
						key={bar?.barId || bar?.id}
						barSnapshot={bar}
						canDeleteBars={canDeleteBars}
						assignMode={assignMode}
						selected={selectedBarIds.has(bar?.barId)}
						status={status}
						onToggleSelect={handleToggleSelectBar}
						legacySubregionNameMap={legacySubregionNameMap}
					/>
				))}
				{loading && paginatedItems.length === 0 && processedBars.length > 0 && (
					<NoResultsContainer>
						<span className="promotionName">Loading page...</span>
					</NoResultsContainer>
				)}
				{processedBars.length === 0 && !loading && (
					<NoResultsContainer>
						<span className="promotionName">No bars found</span>
					</NoResultsContainer>
				)}
			</div>

				<div className="pagination-footer">
					<div className="rows-per-page">
						<span className="label">Rows per page</span>
						<Select
							size="small"
							value={pageSize}
							onChange={handlePageSizeChange}
						>
							{PAGE_SIZE_OPTIONS.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</div>
					<div className="page-controls">
						<span className="page-summary">{pageSummaryText}</span>
						<Pagination
							count={paginationCount}
							page={paginationPage}
							onChange={handlePageChange}
							color="primary"
							shape="rounded"
							showFirstButton
							showLastButton
							siblingCount={1}
							boundaryCount={1}
							disabled={loading && processedBars.length === 0}
						/>
					</div>
				</div>

			<Dialog
				open={confirmAssignOpen}
				onClose={handleCancelAssign}
				aria-labelledby="assign-region-dialog-title"
				aria-describedby="assign-region-dialog-description"
			>
				<DialogTitle id="assign-region-dialog-title">Confirm Region Assignment</DialogTitle>
				<DialogContent>
					<DialogContentText id="assign-region-dialog-description">
						Assign {assignRegionName || "the selected region"} to {selectedBarIds.size} bar
						{selectedBarIds.size === 1 ? "" : "s"}? This will update their region metadata.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelAssign} disabled={assigning}>
						Cancel
					</Button>
					<Button onClick={handleAssignConfirm} color="primary" disabled={assigning}>
						{assigning ? "Assigning..." : "Accept"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;
	overflow: auto;
	padding: 24px;
	background: ${THEME_MAP["color-basic-100"]};

	.assign-actions {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 12px;
		flex-wrap: wrap;
	}


	.assign-region-select {
		min-width: 220px;
	}

	.assign-error {
		color: ${THEME_MAP["color-danger-700"]};
		font-size: 0.9rem;
	}

	.results-meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		color: ${THEME_MAP["color-basic-600"]};
	}

	.results-meta-spacer {
		flex: 1;
	}

	.add-bar-btn {
		background: #1976d2;
		color: white;
		width: 32px;
		height: 32px;

		&:hover {
			background: #1565c0;
		}
	}

	.bars-container {
		width: 100%;
		min-height: 100px;
		border-radius: 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-100"]};
		padding: 0 0 8px;
		margin-bottom: 16px;
		max-height: calc(100% - 180px);
		overflow-y: scroll;
		position: relative;
	}

	.pagination-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px 0;
		border-top: 1px solid ${THEME_MAP["color-basic-300"]};
	}

	.rows-per-page {
		display: flex;
		align-items: center;
		gap: 8px;
		color: ${THEME_MAP["color-basic-700"]};
		font-weight: 600;
	}

	.page-controls {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		justify-content: flex-end;
		min-width: 260px;
		flex-wrap: wrap;
	}

	.page-summary {
		color: ${THEME_MAP["color-basic-700"]};
		white-space: nowrap;
	}

	@media (max-width: 767px) {
		.pagination-footer {
			flex-direction: column;
			align-items: stretch;
			gap: 8px;
			padding: 10px 0;
		}

		.rows-per-page {
			justify-content: center;
		}

		.page-controls {
			flex-direction: column;
			align-items: center;
			min-width: 0;
			gap: 6px;
		}

		.page-controls .MuiPagination-root {
			.MuiPaginationItem-root {
				min-width: 28px;
				height: 28px;
				font-size: 0.8rem;
			}
		}
	}

	.loading-indicator {
		color: ${THEME_MAP["color-primary-600"]};
		font-weight: 600;
		margin-left: 8px;
	}

	.selection-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	@media (max-width: 767px) {
		display: flex;
		flex-direction: column;
		padding: 12px;
		overflow: hidden;

		.bars-container {
			flex: 1;
			min-height: 0;
			max-height: none;
			overflow: auto;
			-webkit-overflow-scrolling: touch;
		}

		.pagination-footer {
			flex-shrink: 0;
		}
	}
`;

const DRAG_THRESHOLD = 8;

function BarListItem({ barSnapshot, assignMode, selected, canDeleteBars, onToggleSelect, status, legacySubregionNameMap }) {
	const [showDeleteAlert, setShowDeleteAlert] = useState(false);
	const [deletePending, setDeletePending] = useState(false);
	const pointerStartRef = useRef(null);
	const wasDragRef = useRef(false);
	const bar = React.useMemo(
		() => normalizeBarSnapshot(barSnapshot),
		[barSnapshot]
	);
	const history = useHistory();
	const { removeBarFromSite } = useUserUtils();
	const stateRegions = useStateRegions();
	const regionIndex = useMemo(() => buildStateRegionIndex(stateRegions), [stateRegions]);

	if (!bar?.barId) {
		return null;
	}

	const handlePointerDown = (e) => {
		pointerStartRef.current = { x: e.clientX, y: e.clientY };
		wasDragRef.current = false;
	};

	const handlePointerUp = (e) => {
		if (pointerStartRef.current) {
			const dx = Math.abs(e.clientX - pointerStartRef.current.x);
			const dy = Math.abs(e.clientY - pointerStartRef.current.y);
			if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
				wasDragRef.current = true;
			}
		}
		pointerStartRef.current = null;
	};

	const handleClick = () => {
		if (wasDragRef.current) {
			wasDragRef.current = false;
			return;
		}
		if (assignMode) {
			if (onToggleSelect) {
				onToggleSelect(bar.barId);
			}
			return;
		}
		history.push(`bar/${bar.barId}`);
	};

	const handleDeleteClick = (event) => {
		event.stopPropagation();
		event.preventDefault();
		if (!canDeleteBars) {
			return;
		}
		setShowDeleteAlert(true);
	};

	const removeBarPermissions = async () => {
		try {
			if (!canDeleteBars) {
				setShowDeleteAlert(false);
				return;
			}
			setDeletePending(true);
			await removeBarFromSite(bar.barId);
			window.location.href = "";
		} catch (err) {
			console.error("Failed to delete bar", err);
			captureError(err, { action: "deleteBar" });
			setDeletePending(false);
		}
	};

	const dialogView = (
		<Dialog
			open={showDeleteAlert}
			onClose={() => setShowDeleteAlert(false)}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">
				{"Are you sure?"}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					Would you like to proceed with deleting this bar?
					<br />
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setShowDeleteAlert(false)}
					color="primary"
					disabled={deletePending}
				>
					Cancel
				</Button>

				<Button
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						removeBarPermissions();
					}}
					color="primary"
					autoFocus
					disabled={deletePending}
				>
					{deletePending ? (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
							<CircularProgress size={16} />
							Deleting...
						</span>
					) : (
						"Delete"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);

	const streetLine = normalizeWhitespace(bar.address_1 || "");
	const city = bar.city || "";
	const state = normalizeStateValue(bar.state || "");
	const { regionName, subregionName } = getStateRegionLabel(
		{ stateId: bar.stateId, regionId: bar.regionId, subregionId: bar.subregionId },
		regionIndex
	);
	const resolvedRegionName = regionName || "—";
	const legacySubregionName = legacySubregionNameMap?.get(bar.subregionId) || "";
	const resolvedSubregionName = subregionName || legacySubregionName || bar.subregionId || "—";
	const showActions = !assignMode;
	const showDeleteAction = showActions && canDeleteBars;
	const resolvedStatus = status || "incomplete";
	const statusStyle = STATUS_STYLE_BY_KEY[resolvedStatus] || STATUS_STYLE_BY_KEY.incomplete;
	const hoverBackground = statusStyle.hoverBackground || statusStyle.background;

	return (
		<>
			<BarItem
				onPointerDown={handlePointerDown}
				onPointerUp={handlePointerUp}
				onClick={handleClick}
				$hasSelect={assignMode}
				$background={statusStyle.background}
				$textColor={statusStyle.textColor}
				$hoverBackground={hoverBackground}
			>
				{assignMode && (
					<div className="cell select" onClick={(event) => event.stopPropagation()}>
						<Checkbox
							checked={selected}
							onChange={() => {
								if (onToggleSelect) {
									onToggleSelect(bar.barId);
								}
							}}
						/>
					</div>
				)}

				<div className="cell name">
					<div className="primary">{bar.barName || "Bar Name"}</div>
				</div>
				<div className="cell region">{resolvedRegionName}</div>
				<div className="cell subregion">{resolvedSubregionName}</div>
				<div className="cell street">{streetLine || "—"}</div>
				<div className="cell city">{city || "—"}</div>
				<div className="cell state">{state || "—"}</div>
				<div className="cell added">{formatAddedDate(bar.createdAt) || "—"}</div>
				<div className="cell actions">
					{showDeleteAction && (
						<IconButton
							size="small"
							onClick={handleDeleteClick}
							title="Delete bar"
							className="icon-button"
						>
							<DeleteOutline fontSize="small" />
						</IconButton>
					)}
				</div>
			</BarItem>
			{dialogView}
		</>
	);
}

const SortChevron = styled(ExpandMoreRounded)`
	font-size: 18px;
	transition: transform 0.2s ease, opacity 0.2s ease, color 0.2s ease;
	transform: rotate(${(props) => (props.$direction === "asc" ? "180deg" : "0deg")});
	color: ${(props) =>
		props.$active
			? THEME_MAP["color-primary-700"]
			: THEME_MAP["color-basic-500"]};
	opacity: ${(props) => (props.$active ? 1 : 0.65)};
`;

const SortDash = styled(RemoveRounded)`
	font-size: 18px;
	color: ${THEME_MAP["color-basic-500"]};
	opacity: 0.5;
`;

const BarHeader = styled.div`
	display: grid;
	grid-template-columns: ${(props) => props.$assignMode ? BAR_GRID_TEMPLATE_ASSIGN : BAR_GRID_TEMPLATE};
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	width: 100%;
	background: ${THEME_MAP["color-basic-100"]};
	color: ${THEME_MAP["color-basic-700"]};
	font-weight: 700;
	position: sticky;
	top: 0;
	z-index: 1;
	border-bottom: 1px solid ${THEME_MAP["color-basic-300"]};

	@media (max-width: 767px) {
		min-width: 900px;
	}

	.header-cell {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		font-size: 0.9rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		padding: 4px 0;
	}

	.header-cell.sortable {
		cursor: pointer;
		user-select: none;
	}

	.header-cell .label {
		min-width: 0;
	}

	.header-cell.actions {
		justify-content: flex-end;
		gap: 6px;
		overflow: visible;
	}

	.header-cell .sort-indicator {
		display: inline-flex;
		align-items: center;
		gap: 2px;
	}

	.header-cell .sort-order {
		font-size: 0.7rem;
		font-weight: 700;
		line-height: 1;
		color: ${THEME_MAP["color-primary-700"]};
	}

	.header-cell .cancel-sort {
		padding: 2px;
		color: ${THEME_MAP["color-basic-500"]};
	}

	.header-cell .cancel-sort:hover {
		color: ${THEME_MAP["color-primary-700"]};
	}
`;

const BarItem = styled.div`
	display: grid;
	grid-template-columns: ${(props) => props.$hasSelect ? BAR_GRID_TEMPLATE_ASSIGN : BAR_GRID_TEMPLATE};
	align-items: center;
	gap: 8px;
	padding: 10px 16px;
	width: 100%;
	margin: 4px 0;
	background: ${(props) => props.$background || "transparent"};
	color: ${(props) => props.$textColor || THEME_MAP["color-basic-700"]};
	border-radius: 2px;
	transition: background 0.12s ease, filter 0.12s ease;

	@media (max-width: 767px) {
		min-width: 900px;
	}

	.cell {
		min-width: 0;
		color: inherit;
		font-size: 0.95rem;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.cell.name .primary {
		color: inherit;
		font-weight: 600;
		font-size: 1.05rem;
	}

	.cell.actions {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 6px;
	}

	.cell.select {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.icon-button {
		color: inherit;
	}
`;

const NoResultsContainer = styled.div`
	padding: 8px 24px;
`;
